import React from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import useNow from "../../hooks/useNow";
import { getWaitingState, localTimeZone } from "../../utils/waitingTime";
import ExactTime from "../ExactTime";

export const useWaitingStyles = makeStyles(theme => ({
  "@keyframes waitingGlow": {
    "0%, 100%": { boxShadow: "0 0 0 0 var(--waiting-glow)" },
    "50%": { boxShadow: "0 0 0 3px var(--waiting-glow)" },
  },
  short: { "--waiting-color": theme.palette.type === "dark" ? "#6EE7B7" : "#047857", "--waiting-bg": "rgba(16,185,129,.10)", "--waiting-glow": "rgba(16,185,129,.12)" },
  medium: { "--waiting-color": theme.palette.type === "dark" ? "#FDBA74" : "#B45309", "--waiting-bg": "rgba(245,158,11,.10)", "--waiting-glow": "rgba(245,158,11,.14)" },
  long: { "--waiting-color": theme.palette.type === "dark" ? "#FDA4AF" : "#BE123C", "--waiting-bg": "rgba(244,63,94,.09)", "--waiting-glow": "rgba(244,63,94,.17)" },
  unknown: { "--waiting-color": theme.palette.text.secondary, "--waiting-bg": "rgba(148,163,184,.1)", "--waiting-glow": "transparent" },
  frame: {
    "&&": { border: "1px solid var(--waiting-color)", borderLeftWidth: 4 },
    "&$medium": { animation: "$waitingGlow 5s ease-in-out infinite" },
    "&$long": { animation: "$waitingGlow 2.8s ease-in-out infinite" },
    "@media (prefers-reduced-motion: reduce)": { animation: "none !important", transition: "none !important" },
  },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 5, maxWidth: "100%", marginTop: 7,
    padding: "4px 7px", borderRadius: 7, backgroundColor: "var(--waiting-bg)", color: "var(--waiting-color)",
    fontSize: "0.66rem", fontWeight: 700, lineHeight: 1.4, whiteSpace: "nowrap",
  },
  icon: { width: 13, height: 13, flexShrink: 0 },
  timer: { fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum"', letterSpacing: ".025em", whiteSpace: "nowrap" },
  panel: { padding: "20px", borderRadius: 16, color: "var(--waiting-color)", background: "var(--waiting-bg)" },
  panelHeading: { display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: ".85rem" },
  panelTimer: { fontSize: "clamp(1.8rem, 7vw, 2.6rem)", fontWeight: 800, lineHeight: 1.3, margin: "8px 0" },
  panelMeta: { color: theme.palette.text.secondary, fontSize: ".75rem", lineHeight: 1.6 },
  legend: { display: "flex", gap: "6px 12px", flexWrap: "wrap", color: theme.palette.text.secondary, fontSize: ".69rem", margin: "0 0 12px" },
  legendItem: { display: "inline-flex", gap: 5, alignItems: "center" },
  dot: { width: 7, height: 7, borderRadius: "50%", backgroundColor: "var(--waiting-color)" },
}));

export const WaitingLegend = () => {
  const classes = useWaitingStyles();
  return <div className={classes.legend} aria-label="Faixas de tempo de espera">
    {[["short", "Menos de 5 min"], ["medium", "5 a 15 min"], ["long", "15 min ou mais"]].map(([level, label]) => (
      <span key={level} className={classes.legendItem + " " + classes[level]}><span className={classes.dot} aria-hidden="true" />{label}</span>
    ))}
  </div>;
};

const WaitingStatus = ({ ticket, now: suppliedNow, expanded = false }) => {
  const classes = useWaitingStyles();
  const clock = useNow(ticket?.status === "pending" && suppliedNow === undefined);
  const wait = getWaitingState(ticket, suppliedNow === undefined ? clock : suppliedNow);
  if (!wait.active) return null;
  const levelClass = classes[wait.level];
  if (expanded) return (
    <section className={classes.panel + " " + classes.frame + " " + levelClass} aria-label="Tempo de espera do atendimento">
      <div className={classes.panelHeading}><AccessTimeIcon fontSize="small" />{wait.label}</div>
      <div className={classes.panelTimer + " " + classes.timer} role="timer" aria-live="off" aria-label="Tempo decorrido na fila">
        {wait.lowerBound ? "≥ " : wait.estimated ? "≈ " : ""}{wait.timer}
      </div>
      <div className={classes.panelMeta}>
        {wait.lowerBound ? "Tempo mínimo estimado · referência " : wait.estimated ? "Espera estimada · cadastro em " : "Na fila desde "}
        <ExactTime value={wait.start} label={wait.estimated ? "Referência estimada da espera" : "Entrada na fila"} />
        <div>{localTimeZone()}{wait.lowerBound ? " · Atendimento anterior à atualização" : wait.estimated ? " · Início da espera não registrado" : ""}</div>
      </div>
    </section>
  );
  return (
    <Tooltip title={wait.description} arrow>
      <span className={classes.badge + " " + levelClass}>
        <AccessTimeIcon className={classes.icon} aria-hidden="true" />
        <span>{wait.label}</span>
        <span className={classes.timer} role="timer" aria-live="off" aria-label="Tempo decorrido na fila">{wait.lowerBound ? "≥ " : wait.estimated ? "≈ " : ""}{wait.timer}</span>
      </span>
    </Tooltip>
  );
};
export default WaitingStatus;
